/**
 * Generic, subject-scoped attachments card.
 *
 * The claim Evidence card generalised to any registered attachment subject
 * (`claim`, `transfer`, ...). Same behaviour and the same shared primitives -
 * uploader grouping, `EvidenceFileRow`, the `AddEvidenceDialogShell` add flow,
 * inline preview, audited soft-delete, "download all" - driven off the generic
 * `/files/attachments/<subject_type>/<subject_uuid>/` API via the
 * `use-subject-attachments` hooks. A "+ Add" entry captures a free-text label
 * (stored as the file `category`) and one or more documents.
 *
 * Intentionally free of the claim-only concerns (document-category taxonomy,
 * checklist linkage, attribute-values evidence, per-section filtering) so it
 * stays reusable; the claim card keeps those via its own `EvidenceCard`.
 */

import { useState } from "react";
import {
  ChevronDown,
  Download,
  Image as ImageIcon,
  Loader2,
  Plus,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import {
  useDeleteSubjectAttachment,
  useRecategoriseSubjectAttachment,
  useSubjectAttachments,
  useUploadSubjectEvidence,
  type Attachment,
} from "@/admin/hooks/use-subject-attachments";
import { isFiled } from "@/admin/components/shared/attachments-card-utils";
import { DetailCard } from "@/admin/components/shared/detail-card";
import { AuthenticatedImage } from "@/admin/components/authenticated-image";
import api from "@/admin/lib/api";
import { toastApiError } from "@/shared/lib/api-error";
import { useFormatters } from "@/shared/lib/formatting";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import {
  FileViewerDialog,
  type ViewableFile,
} from "@/shared/components/file-viewer-dialog";
import { AddEvidenceDialogShell } from "@/shared/components/evidence/add-evidence-dialog-shell";
import { EvidenceFileRow } from "@/shared/components/evidence/evidence-file-row";
import { formatBytes } from "@/shared/components/evidence/evidence-file-utils";
import { humanizeAccept, type UploadedFileItem } from "@/shared/lib/file-types";
import { cn } from "@/shared/lib/utils";

const ACCEPT =
  "image/jpeg,image/png,image/heic,image/heif,.heic,application/pdf,application/msword," +
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document," +
  "application/vnd.ms-excel," +
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet," +
  "text/plain,video/mp4,video/quicktime";

const UPLOADER_ROLE_LABEL: Record<string, string> = {
  staff: "Admin",
  consumer: "Customer",
  contractor: "Contractor",
  third_party: "Third party",
};
const RECIPIENT_LABEL: Record<string, string> = {
  consumer: "Consumer",
  contractor: "Contractor",
  third_party: "Third party",
  staff: "Staff",
};

function uploaderGroupKey(u: Attachment["uploader"]): string {
  return (u.email || u.name || u.kind || "unknown").toLowerCase();
}
function uploaderGroupLabel(u: Attachment["uploader"]): string {
  if (u.name) return u.name;
  if (u.email) return u.email;
  return (
    UPLOADER_ROLE_LABEL[u.kind] ?? RECIPIENT_LABEL[u.kind] ?? "Unattributed"
  );
}

interface UploaderGroup {
  key: string;
  label: string;
  kind: string;
  rows: Attachment[];
}

function groupRowsByUploader(rows: Attachment[]): UploaderGroup[] {
  const groups: UploaderGroup[] = [];
  const byKey = new Map<string, UploaderGroup>();
  for (const att of rows) {
    const key = uploaderGroupKey(att.uploader);
    let group = byKey.get(key);
    if (!group) {
      group = {
        key,
        label: uploaderGroupLabel(att.uploader),
        kind: att.uploader.kind || "",
        rows: [],
      };
      byKey.set(key, group);
      groups.push(group);
    }
    group.rows.push(att);
  }
  return groups;
}

export function AttachmentsCard({
  subjectType,
  subjectUuid,
  useCase,
  title,
  canAdd = false,
  canDelete = false,
  canRelabel,
  addTitle,
  labelPlaceholder,
  emptyText,
  icon,
  onChanged,
}: {
  subjectType: string;
  subjectUuid: string;
  useCase: string;
  title?: string;
  canAdd?: boolean;
  canDelete?: boolean;
  /**
   * Whether labels on existing documents can be edited. Defaults to
   * `canDelete`, which is the gate the backend applies - re-filing a
   * document is treated as the same order of authority as removing it.
   */
  canRelabel?: boolean;
  addTitle?: string;
  labelPlaceholder?: string;
  emptyText?: string;
  icon?: React.ReactNode;
  /**
   * Called after a successful upload or delete. Lets the host react to a
   * change that may have side effects beyond the attachment list - e.g. a
   * transfer advancing into its document-review stage on upload, so the
   * host can refresh the workflow action menu.
   */
  onChanged?: () => void;
}) {
  const { t } = useTranslation();
  const resolvedTitle = title ?? t("attachments.title", "Documents");
  const resolvedAddTitle = addTitle ?? t("attachments.add", "Add document");
  const resolvedLabelPlaceholder =
    labelPlaceholder ??
    t("attachments.labelPlaceholder", "e.g. Proof of ownership");
  const resolvedEmptyText =
    emptyText ?? t("attachments.empty", "No documents attached");
  const resolvedCanRelabel = canRelabel ?? canDelete;

  const [showDeleted, setShowDeleted] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [previewing, setPreviewing] = useState<Attachment | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Attachment | null>(null);
  const [pendingRelabel, setPendingRelabel] = useState<Attachment | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    () => new Set(),
  );

  const query = useSubjectAttachments(subjectType, subjectUuid, {
    includeDeleted: showDeleted,
  });
  const deleteMutation = useDeleteSubjectAttachment(subjectType, subjectUuid);
  const relabelMutation = useRecategoriseSubjectAttachment(
    subjectType,
    subjectUuid,
  );

  const attachments = Array.isArray(query.data) ? query.data : [];
  const liveCount = attachments.filter((a) => !a.is_deleted).length;
  const deletedCount = attachments.filter((a) => a.is_deleted).length;
  const groups = groupRowsByUploader(attachments);

  const toggleGroup = (key: string) =>
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const handleDownloadAll = async () => {
    setDownloadingAll(true);
    try {
      const res = await api.get(
        `/files/attachments/${subjectType}/${subjectUuid}/download-all/`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${subjectType}-${subjectUuid.slice(0, 8)}-documents.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      toastApiError(err, "Couldn't download the documents.");
    } finally {
      setDownloadingAll(false);
    }
  };

  return (
    <>
      <DetailCard
        title={resolvedTitle}
        icon={icon ?? <ImageIcon className="h-3.5 w-3.5" />}
        trailing={
          <div className="flex items-center gap-3">
            {liveCount > 0 ? (
              <Button
                type="button"
                variant="ghost"
                size="xs"
                onClick={handleDownloadAll}
                disabled={downloadingAll}
                className="inline-flex cursor-pointer items-center gap-1 bg-transparent p-0 text-[10px] font-normal uppercase tracking-[0.1em] text-[var(--ink-3)] transition-opacity duration-150 hover:opacity-70 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {downloadingAll ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Download className="h-3 w-3" />
                )}
                Download all
              </Button>
            ) : null}
            {deletedCount > 0 || showDeleted ? (
              <Button
                type="button"
                variant="ghost"
                size="xs"
                onClick={() => setShowDeleted((v) => !v)}
                className="inline-flex cursor-pointer items-center gap-1 bg-transparent p-0 text-[10px] font-normal uppercase tracking-[0.1em] text-[var(--ink-3)] transition-opacity duration-150 hover:opacity-70"
              >
                {showDeleted
                  ? "Hide deleted"
                  : `Show deleted${deletedCount ? ` (${deletedCount})` : ""}`}
              </Button>
            ) : null}
            {canAdd ? (
              <Button
                type="button"
                variant="ghost"
                size="xs"
                onClick={() => setAddOpen(true)}
                className="inline-flex cursor-pointer items-center gap-1 bg-transparent p-0 text-[10px] font-normal uppercase tracking-[0.1em] text-[var(--green)] transition-opacity duration-150 hover:opacity-70"
              >
                <Plus className="h-3 w-3" />
                Add
              </Button>
            ) : null}
          </div>
        }
      >
        {query.isPending ? (
          <div className="flex items-center justify-center py-6 text-[11px] text-[var(--ink-3)]">
            <Loader2 className="mr-2 h-3 w-3 animate-spin" />
            {t("attachments.loading", "Loading documents...")}
          </div>
        ) : query.isError ? (
          <div className="px-3 py-4 text-center text-[11px] text-rose-600">
            {t(
              "attachments.loadError",
              "Couldn't load documents. Try refreshing.",
            )}
          </div>
        ) : attachments.length === 0 ? (
          <div className="px-3 py-4 text-center text-[11px] text-[var(--ink-3)]">
            {resolvedEmptyText}
          </div>
        ) : (
          <ul className="divide-y divide-[var(--rule)]">
            {groups.map((group) => {
              const isCollapsed = !expandedGroups.has(group.key);
              const roleLabel =
                UPLOADER_ROLE_LABEL[group.kind] ?? RECIPIENT_LABEL[group.kind];
              return (
                <li key={group.key}>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => toggleGroup(group.key)}
                    aria-expanded={!isCollapsed}
                    className="flex h-auto w-full items-center justify-start gap-2 rounded-none bg-[var(--bg-light)] px-3 py-2 hover:bg-[var(--bg-light)]"
                  >
                    <ChevronDown
                      className={cn(
                        "h-3.5 w-3.5 shrink-0 text-[var(--ink-3)] transition-transform duration-150",
                        isCollapsed && "-rotate-90",
                      )}
                    />
                    <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--ink-2)]">
                      {group.label}
                    </span>
                    {roleLabel &&
                    roleLabel.toLowerCase() !== group.label.toLowerCase() ? (
                      <Badge
                        variant="secondary"
                        className="text-[9px] uppercase tracking-wider"
                      >
                        {roleLabel}
                      </Badge>
                    ) : null}
                    <span className="text-[10px] text-[var(--ink-3)]">
                      {group.rows.length}
                    </span>
                  </Button>
                  {!isCollapsed ? (
                    <ul className="divide-y divide-[var(--rule)] border-t border-[var(--rule)]">
                      {group.rows.map((att) => (
                        <AttachmentRow
                          key={att.uuid}
                          att={att}
                          canDelete={canDelete && isFiled(att)}
                          canRelabel={resolvedCanRelabel && isFiled(att)}
                          onPreview={() => setPreviewing(att)}
                          onRequestDelete={() => setPendingDelete(att)}
                          onRequestRelabel={() => setPendingRelabel(att)}
                          isDeleting={
                            deleteMutation.isPending &&
                            deleteMutation.variables?.uuid === att.uuid
                          }
                        />
                      ))}
                    </ul>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </DetailCard>

      <FileViewerDialog
        file={
          previewing
            ? ({
                file_uuid: previewing.uuid,
                file_url: previewing.file_url,
                file_name: previewing.file_name,
                mime_type: previewing.mime_type,
              } satisfies ViewableFile)
            : null
        }
        onOpenChange={(open) => {
          if (!open) setPreviewing(null);
        }}
      />

      {pendingDelete ? (
        <Dialog
          open
          onOpenChange={(next) => {
            if (!next && !deleteMutation.isPending) setPendingDelete(null);
          }}
        >
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Delete document</DialogTitle>
              <DialogDescription>
                Remove{" "}
                <span className="font-medium text-[var(--ink)]">
                  {pendingDelete.file_name}
                </span>
                ? The file stays in storage for audit.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setPendingDelete(null)}
                disabled={deleteMutation.isPending}
              >
                {t("attachments.cancel", "Cancel")}
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={() =>
                  deleteMutation.mutate(
                    { uuid: pendingDelete.uuid },
                    {
                      onSuccess: () => {
                        setPendingDelete(null);
                        onChanged?.();
                      },
                    },
                  )
                }
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                    Deleting…
                  </>
                ) : (
                  "Delete"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}

      {pendingRelabel ? (
        <RelabelAttachmentDialog
          att={pendingRelabel}
          labelPlaceholder={resolvedLabelPlaceholder}
          isSaving={relabelMutation.isPending}
          onClose={() => setPendingRelabel(null)}
          onSave={(category) =>
            relabelMutation.mutate(
              { uuid: pendingRelabel.uuid, category },
              {
                onSuccess: () => {
                  setPendingRelabel(null);
                  onChanged?.();
                },
              },
            )
          }
        />
      ) : null}

      {addOpen ? (
        <AddAttachmentDialog
          subjectType={subjectType}
          subjectUuid={subjectUuid}
          useCase={useCase}
          title={resolvedAddTitle}
          labelPlaceholder={resolvedLabelPlaceholder}
          onUploaded={onChanged}
          onClose={() => setAddOpen(false)}
        />
      ) : null}
    </>
  );
}

function AttachmentRow({
  att,
  canDelete,
  canRelabel,
  onPreview,
  onRequestDelete,
  onRequestRelabel,
  isDeleting,
}: {
  att: Attachment;
  canDelete: boolean;
  canRelabel: boolean;
  onPreview: () => void;
  onRequestDelete: () => void;
  onRequestRelabel: () => void;
  isDeleting: boolean;
}) {
  const { t } = useTranslation();
  const { formatDateTime } = useFormatters();
  return (
    <EvidenceFileRow
      fileName={att.file_name}
      fileUuid={att.uuid}
      fileUrl={att.file_url}
      thumbnailUrl={att.thumbnail_url}
      thumbnail={
        <AuthenticatedImage
          src={att.thumbnail_url ?? att.file_url}
          alt={att.file_name}
          className="h-full w-full object-cover"
          fallback={null}
        />
      }
      mimeType={att.mime_type}
      deleted={att.is_deleted}
      onPreview={onPreview}
      onDelete={canDelete ? onRequestDelete : undefined}
      onRecategorise={canRelabel ? onRequestRelabel : undefined}
      recategoriseLabel={t("attachments.relabelTitle", "Change label")}
      isDeleting={isDeleting}
      badges={
        att.category ? (
          <Badge
            variant="secondary"
            className="text-[9px] uppercase tracking-wider"
          >
            {att.category}
          </Badge>
        ) : null
      }
      meta={
        <>
          <span>{formatBytes(att.file_size)}</span>
          <span>·</span>
          <span>{formatDateTime(att.uploaded_at)}</span>
        </>
      }
      deletedNote={
        <>
          Deleted {att.deleted_at ? `on ${formatDateTime(att.deleted_at)}` : ""}
          {att.deleted_by_email ? ` by ${att.deleted_by_email}` : ""}
          {att.deleted_reason ? ` - ${att.deleted_reason}` : ""}
        </>
      }
    />
  );
}

/**
 * Rename an existing document's label.
 *
 * Single free-text field, pre-filled with the current label - the same
 * value the add dialog's classifier captures, so a mis-typed label is
 * fixable without deleting and re-uploading the file (which would lose its
 * uploader attribution and date). Mirrors the claim Evidence card's
 * "change category" dialog, except the label here is free text rather than
 * a fixed taxonomy.
 */
function RelabelAttachmentDialog({
  att,
  labelPlaceholder,
  isSaving,
  onSave,
  onClose,
}: {
  att: Attachment;
  labelPlaceholder: string;
  isSaving: boolean;
  onSave: (category: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [label, setLabel] = useState(att.category ?? "");
  const trimmed = label.trim();
  const unchanged = trimmed === (att.category ?? "").trim();
  const canSave = trimmed.length > 0 && !unchanged && !isSaving;

  return (
    <Dialog open onOpenChange={(next) => !next && !isSaving && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t("attachments.relabelTitle", "Change label")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "attachments.relabelDescription",
              "Rename the label on {{fileName}}. The file itself, its uploader and its upload date are unchanged.",
              { fileName: att.file_name },
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <Label className="text-[12px] font-medium uppercase tracking-[0.1em] text-[var(--ink-3)]">
            {t("attachments.labelField", "Label")}{" "}
            <span className="text-[var(--status-error)]">*</span>
          </Label>
          <Input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder={labelPlaceholder}
            maxLength={64}
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isSaving}
          >
            {t("attachments.cancel", "Cancel")}
          </Button>
          <Button
            type="button"
            onClick={() => onSave(trimmed)}
            disabled={!canSave}
          >
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                {t("attachments.saving", "Saving...")}
              </>
            ) : (
              t("attachments.saveLabel", "Save label")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddAttachmentDialog({
  subjectType,
  subjectUuid,
  useCase,
  title,
  labelPlaceholder,
  onUploaded,
  onClose,
}: {
  subjectType: string;
  subjectUuid: string;
  useCase: string;
  title: string;
  labelPlaceholder: string;
  onUploaded?: () => void;
  onClose: () => void;
}) {
  const upload = useUploadSubjectEvidence(subjectType, subjectUuid, useCase);
  const [label, setLabel] = useState("");
  const [added, setAdded] = useState<UploadedFileItem[]>([]);

  const labelReady = label.trim().length > 0;

  const uploadFn = async (file: File): Promise<UploadedFileItem> => {
    try {
      const r = await upload.mutateAsync({ file, category: label.trim() });
      onUploaded?.();
      return {
        uuid: r.uuid,
        file_name: r.file_name ?? file.name,
        file_url: r.file_url,
        mime_type: r.mime_type,
      };
    } catch (err) {
      toastApiError(err, "Failed to add document");
      throw err;
    }
  };

  const close = () => {
    setLabel("");
    setAdded([]);
    onClose();
  };

  const classifier = (
    <div className="flex flex-col gap-2">
      <Label className="text-[12px] font-medium uppercase tracking-[0.1em] text-[var(--ink-3)]">
        Label <span className="text-[var(--status-error)]">*</span>
      </Label>
      <Input
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        placeholder={labelPlaceholder}
        maxLength={64}
        autoFocus
      />
    </div>
  );

  return (
    <AddEvidenceDialogShell
      title={title}
      description="Give the documents a label, then upload one or more files."
      classifier={classifier}
      classifierReady={labelReady}
      value={added}
      onChange={setAdded}
      uploadFn={uploadFn}
      accept={ACCEPT}
      acceptLabel={humanizeAccept(ACCEPT)}
      multiple
      notReadyHint="Enter a label first"
      onClose={close}
    />
  );
}
